import { useCallback, useState } from "react";
import { debounce } from "lodash";
import { FixedSizeList as List, ListChildComponentProps } from "react-window";
import { apiRequest } from "./lib/queryClient";
import PopupModal from "./Dashboard/PopupModel";
import { ApiResponse } from "./lib/utils";



interface StockModalProps {
  show: boolean;
  onClose: () => void;
  onAddStocks: (stocks: any) => void;
}

interface SearchParams {
  query: string;
  category: string;
}

export interface InstrumentRecord {
  exchange_segment: string;   // e.g., "NSE_EQ"
  fno_true: boolean;          // e.g., false
  instrument: string;         // e.g., "EQUITY"
  instrument_type: string;    // e.g., "ES"
  security_id: number;        // e.g., 10217
  symbol_name: string;        // e.g., "ADANI ENERGY SOLUTION LTD"
  underlying_symbol: string;  // e.g., "ADANIENSOL"
}

export const CustomModelAddStocks: React.FC<StockModalProps> = ({ 
  show,
  onClose, 
  onAddStocks 
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Equity");
  const [loading, setLoading] = useState(false);
  const [filteredStocks, setFilteredStocks] = useState<any[]>([]);
  const [selectedStocks, setSelectedStocks] = useState<any[]>([]);
  
  const categories = ["Equity", "FNO", "Currency", "Commodity","Index"];

  const getSearchInstruments = async (params: SearchParams):Promise<InstrumentRecord[]> => {
    try {
      const response = await apiRequest<ApiResponse<InstrumentRecord[]>>(
        "GET", 
        "api/search-instruments-dhan",
        null, 
        params
      );
      return response.data|| [];
    } catch (error) {
      console.error("Error fetching instruments:", error);
      return [];
    }
  };

  const fetchFilteredStocks = async (query: string, category: string = selectedCategory) => {
    setLoading(true);
    if (!query.trim()) {
      setFilteredStocks([]);
      setLoading(false);
      return;
    }

    try {
      const stocks_data: InstrumentRecord[] = await getSearchInstruments({ query, category });
      setFilteredStocks(stocks_data);
   
    } catch (err) {
      console.error("Error fetching filtered stocks:", err);
      setFilteredStocks([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchStocks = useCallback(
    debounce((query: string) => {
      fetchFilteredStocks(query, selectedCategory);
    }, 400),
    [selectedCategory]
  );

  const handleStockSelection = (stock: any) => {
    setSelectedStocks((prev) =>
      prev.some((s) => s.security_id === stock.security_id)
        ? prev.filter((s) => s.security_id !== stock.security_id)
        : [...prev, stock]
    );
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    if (searchQuery.trim()) {
      fetchFilteredStocks(searchQuery, category);
    }
  };

  const handleAddStocks = () => {
    onAddStocks(selectedStocks);
    onClose();
  };

  const Row = ({ index, style, data }: ListChildComponentProps<any[]>) => {
    const stock = data[index];
    const isSelected = selectedStocks.some(
      (s) => s.security_id === stock.security_id
    );

    return (
      <div
        style={style}
        className={`flex items-center justify-between px-4 py-2 ${
          isSelected ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
      >
        <div className="leading-tight">
          <div className="font-medium flex items-center gap-2">
            {stock.symbol_name}
            {stock.fno_true && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-800 border border-purple-300">
                FNO
              </span>
            )}
          </div>
          <div className="text-sm text-gray-500">
            {stock.underlying_symbol}{" "}
            {stock.instrument_type && `• ${stock.instrument_type}`}
          </div>
        </div>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => handleStockSelection(stock)}
          className="h-4 w-4 text-blue-600 rounded"
        />
      </div>
    );
  };


  if (!show) return null;


  return (
    <PopupModal title="Add Stocks to Bucket" width="500px" onClose={onClose}>
      <div className="space-y-4">
        <input
          type="text"
          placeholder="Search stocks..."
          className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            debouncedFetchStocks(e.target.value);
          }}
        />

        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`px-3 py-1 text-sm rounded border transition-colors ${
                selectedCategory === category
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="h-64 overflow-y-auto border rounded">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredStocks.length > 0 ? (
            <List
              height={250}
              itemCount={filteredStocks.length}
              itemSize={50}
              width="100%"
              itemData={filteredStocks}
            >
              {Row}
            </List>
          ) : (
            <div className="text-sm text-gray-400 text-center p-4">
              {searchQuery.trim() ? "No results found" : "Start typing to search"}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2">
          <button
            className="px-4 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={`px-4 py-2 text-sm text-white rounded-md transition-colors ${
              selectedStocks.length === 0
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
            disabled={selectedStocks.length === 0}
            onClick={handleAddStocks}
          >
            Add Selected ({selectedStocks.length})
          </button>
        </div>
      </div>
    </PopupModal>
  );
};